import { useMutation, useQuery, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query'
import { TUseMutationOptions, TUseQueryOptions, TCustomUseQueryMeta } from 'core/query/type'
import {
  TGetRealPersonAuthGetVerifyResultParams,
  TGetRealPersonAuthGetVerifyResultResponse,
  getRealPersonAuthGetVerifyResult,
  TGetIdentityGetVerifyRecordParams,
  TGetIdentityGetVerifyRecordResponse,
  getIdentityGetVerifyRecord,
  TPostIdentityStandardCheckParams,
  TPostIdentityStandardCheckResponse,
  postIdentityStandardCheck,
  TPostKycSumsubAccessTokensParams,
  TPostKycSumsubAccessTokensResponse,
  postKycSumsubAccessTokens,
  TPostKycSumsubEventsParams,
  TPostKycSumsubEventsResponse,
  postKycSumsubEvents,
  TPostKycSumsubEventsTestParams,
  TPostKycSumsubEventsTestResponse,
  postKycSumsubEventsTest,
  TPostKycSumsubReviewStatusParams,
  TPostKycSumsubReviewStatusResponse,
  postKycSumsubReviewStatus,
  TPostTestKycSumsubAccessTokensParams,
  TPostTestKycSumsubAccessTokensResponse,
  postTestKycSumsubAccessTokens,
  TPostTestKycSumsubReviewStatusParams,
  TPostTestKycSumsubReviewStatusResponse,
  postTestKycSumsubReviewStatus,
  TPostTestKycSumsubTestCompletedParams,
  TPostTestKycSumsubTestCompletedResponse,
  postTestKycSumsubTestCompleted,
  TPostKycAmlbotCallbackParams,
  TPostKycAmlbotCallbackResponse,
  postKycAmlbotCallback,
  TPostKycAmlbotFormUrlParams,
  TPostKycAmlbotFormUrlResponse,
  postKycAmlbotFormUrl,
  TPostTestKycAmlbotFormUrlParams,
  TPostTestKycAmlbotFormUrlResponse,
  postTestKycAmlbotFormUrl,
  TPostKycAmlbotInitAuthenticationParams,
  TPostKycAmlbotInitAuthenticationResponse,
  postKycAmlbotInitAuthentication,
  TGetRealPersonAuthGetVerifyTokenParams,
  TGetRealPersonAuthGetVerifyTokenResponse,
  getRealPersonAuthGetVerifyToken,
  TPostIdentityCompanyApplyParams,
  TPostIdentityCompanyApplyResponse,
  postIdentityCompanyApply,
  TGetIdentityCompanyFilesInfoParams,
  TGetIdentityCompanyFilesInfoResponse,
  getIdentityCompanyFilesInfo,
  TPostIdentityCompanySaveFilesParams,
  TPostIdentityCompanySaveFilesResponse,
  postIdentityCompanySaveFiles,
  TPostIdentityStandardApplyParams,
  TPostIdentityStandardApplyResponse,
  postIdentityStandardApply,
  TPostIdentityAdvancedApplyParams,
  TPostIdentityAdvancedApplyResponse,
  postIdentityAdvancedApply,
  TGetIdentityCompanyBeneficiaryInfoParams,
  TGetIdentityCompanyBeneficiaryInfoResponse,
  getIdentityCompanyBeneficiaryInfo,
  TGetIdentityCompanyDirectorInfoParams,
  TGetIdentityCompanyDirectorInfoResponse,
  getIdentityCompanyDirectorInfo,
  TPostIdentityCompanySaveBeneficiaryParams,
  TPostIdentityCompanySaveBeneficiaryResponse,
  postIdentityCompanySaveBeneficiary,
  TPostIdentityCompanySaveDirectorParams,
  TPostIdentityCompanySaveDirectorResponse,
  postIdentityCompanySaveDirector,
  TPostIdentityCompanySaveTraderParams,
  TPostIdentityCompanySaveTraderResponse,
  postIdentityCompanySaveTrader,
  TGetIdentityCompanyTraderInfoParams,
  TGetIdentityCompanyTraderInfoResponse,
  getIdentityCompanyTraderInfo,
  TGetIdentityStatusParams,
  TGetIdentityStatusResponse,
  getIdentityStatus,
  TGetIdentityGetUserRightsParams,
  TGetIdentityGetUserRightsResponse,
  getIdentityGetUserRights,
  TGetIdentityGetVerifyResultParams,
  TGetIdentityGetVerifyResultResponse,
  getIdentityGetVerifyResult,
  TGetIdentityCompanyBasicInfoParams,
  TGetIdentityCompanyBasicInfoResponse,
  getIdentityCompanyBasicInfo,
  TPostIdentityCompanySaveBasicInfoParams,
  TPostIdentityCompanySaveBasicInfoResponse,
  postIdentityCompanySaveBasicInfo,
  TGetIdentityCompanyTemplateFileParams,
  TGetIdentityCompanyTemplateFileResponse,
  getIdentityCompanyTemplateFile,
} from 'core/service/yapi/hk-web/RealPersonAuth/api'
// 实人认证
// 获取认证结果
export const useGetRealPersonAuthGetVerifyResultQuery = (
  params?: TGetRealPersonAuthGetVerifyResultParams,
  options?: TUseQueryOptions<TGetRealPersonAuthGetVerifyResultResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'RealPersonAuthGetVerifyResult', 'GET', ...Object.values(params || {})],
    queryFn: () => getRealPersonAuthGetVerifyResult(params, options?.meta?.requestOptions),
  })
}

// 回显认证数据
export const useGetIdentityGetVerifyRecordQuery = (
  params?: TGetIdentityGetVerifyRecordParams,
  options?: TUseQueryOptions<TGetIdentityGetVerifyRecordResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'IdentityGetVerifyRecord', 'GET', ...Object.values(params || {})],
    queryFn: () => getIdentityGetVerifyRecord(params, options?.meta?.requestOptions),
  })
}

// 个人标准认证-检验
export const usePostIdentityStandardCheckMutation = (
  mutationOptions?: TUseMutationOptions<TPostIdentityStandardCheckResponse, TPostIdentityStandardCheckParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'IdentityStandardCheck', 'Post'],
    mutationFn: (params?: TPostIdentityStandardCheckParams) =>
      postIdentityStandardCheck(params, mutationOptions?.meta?.requestOptions),
  })
}

// sumsub生成访问令牌
export const usePostKycSumsubAccessTokensMutation = (
  mutationOptions?: TUseMutationOptions<TPostKycSumsubAccessTokensResponse, TPostKycSumsubAccessTokensParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'KycSumsubAccessTokens', 'Post'],
    mutationFn: (params?: TPostKycSumsubAccessTokensParams) =>
      postKycSumsubAccessTokens(params, mutationOptions?.meta?.requestOptions),
  })
}

// 接受Sumsub Webhook事件
export const usePostKycSumsubEventsMutation = (
  mutationOptions?: TUseMutationOptions<TPostKycSumsubEventsResponse, TPostKycSumsubEventsParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'KycSumsubEvents', 'Post'],
    mutationFn: (params?: TPostKycSumsubEventsParams) =>
      postKycSumsubEvents(params, mutationOptions?.meta?.requestOptions),
  })
}

// 接受Sumsub Webhook事件
export const usePostKycSumsubEventsTestMutation = (
  mutationOptions?: TUseMutationOptions<TPostKycSumsubEventsTestResponse, TPostKycSumsubEventsTestParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'KycSumsubEventsTest', 'Post'],
    mutationFn: (params?: TPostKycSumsubEventsTestParams) =>
      postKycSumsubEventsTest(params, mutationOptions?.meta?.requestOptions),
  })
}

// sumsub查询审核结果
export const usePostKycSumsubReviewStatusMutation = (
  mutationOptions?: TUseMutationOptions<TPostKycSumsubReviewStatusResponse, TPostKycSumsubReviewStatusParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'KycSumsubReviewStatus', 'Post'],
    mutationFn: (params?: TPostKycSumsubReviewStatusParams) =>
      postKycSumsubReviewStatus(params, mutationOptions?.meta?.requestOptions),
  })
}

// sumsub生成访问令牌Test
export const usePostTestKycSumsubAccessTokensMutation = (
  mutationOptions?: TUseMutationOptions<
    TPostTestKycSumsubAccessTokensResponse,
    TPostTestKycSumsubAccessTokensParams
  > & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'TestKycSumsubAccessTokens', 'Post'],
    mutationFn: (params?: TPostTestKycSumsubAccessTokensParams) =>
      postTestKycSumsubAccessTokens(params, mutationOptions?.meta?.requestOptions),
  })
}

// sumsub查询审核结果
export const usePostTestKycSumsubReviewStatusMutation = (
  mutationOptions?: TUseMutationOptions<
    TPostTestKycSumsubReviewStatusResponse,
    TPostTestKycSumsubReviewStatusParams
  > & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'TestKycSumsubReviewStatus', 'Post'],
    mutationFn: (params?: TPostTestKycSumsubReviewStatusParams) =>
      postTestKycSumsubReviewStatus(params, mutationOptions?.meta?.requestOptions),
  })
}

// 在沙盒中为申请人设置审核
export const usePostTestKycSumsubTestCompletedMutation = (
  mutationOptions?: TUseMutationOptions<
    TPostTestKycSumsubTestCompletedResponse,
    TPostTestKycSumsubTestCompletedParams
  > & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'TestKycSumsubTestCompleted', 'Post'],
    mutationFn: (params?: TPostTestKycSumsubTestCompletedParams) =>
      postTestKycSumsubTestCompleted(params, mutationOptions?.meta?.requestOptions),
  })
}

// amlbot 回调
export const usePostKycAmlbotCallbackMutation = (
  mutationOptions?: TUseMutationOptions<TPostKycAmlbotCallbackResponse, TPostKycAmlbotCallbackParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'KycAmlbotCallback', 'Post'],
    mutationFn: (params?: TPostKycAmlbotCallbackParams) =>
      postKycAmlbotCallback(params, mutationOptions?.meta?.requestOptions),
  })
}

// 生成amlbot表单URL
export const usePostKycAmlbotFormUrlMutation = (
  mutationOptions?: TUseMutationOptions<TPostKycAmlbotFormUrlResponse, TPostKycAmlbotFormUrlParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'KycAmlbotFormUrl', 'Post'],
    mutationFn: (params?: TPostKycAmlbotFormUrlParams) =>
      postKycAmlbotFormUrl(params, mutationOptions?.meta?.requestOptions),
  })
}

// 生成amlbot表单URL
export const usePostTestKycAmlbotFormUrlMutation = (
  mutationOptions?: TUseMutationOptions<TPostTestKycAmlbotFormUrlResponse, TPostTestKycAmlbotFormUrlParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'TestKycAmlbotFormUrl', 'Post'],
    mutationFn: (params?: TPostTestKycAmlbotFormUrlParams) =>
      postTestKycAmlbotFormUrl(params, mutationOptions?.meta?.requestOptions),
  })
}

// amlbot初始化数据
export const usePostKycAmlbotInitAuthenticationMutation = (
  mutationOptions?: TUseMutationOptions<
    TPostKycAmlbotInitAuthenticationResponse,
    TPostKycAmlbotInitAuthenticationParams
  > & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'KycAmlbotInitAuthentication', 'Post'],
    mutationFn: (params?: TPostKycAmlbotInitAuthenticationParams) =>
      postKycAmlbotInitAuthentication(params, mutationOptions?.meta?.requestOptions),
  })
}

// 获取认证token
export const useGetRealPersonAuthGetVerifyTokenQuery = (
  params?: TGetRealPersonAuthGetVerifyTokenParams,
  options?: TUseQueryOptions<TGetRealPersonAuthGetVerifyTokenResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'RealPersonAuthGetVerifyToken', 'GET', ...Object.values(params || {})],
    queryFn: () => getRealPersonAuthGetVerifyToken(params, options?.meta?.requestOptions),
  })
}

// 企业认证-提交审核
export const usePostIdentityCompanyApplyMutation = (
  mutationOptions?: TUseMutationOptions<TPostIdentityCompanyApplyResponse, TPostIdentityCompanyApplyParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'IdentityCompanyApply', 'Post'],
    mutationFn: (params?: TPostIdentityCompanyApplyParams) =>
      postIdentityCompanyApply(params, mutationOptions?.meta?.requestOptions),
  })
}

// 企业认证-查询上传文件草稿
export const useGetIdentityCompanyFilesInfoQuery = (
  params?: TGetIdentityCompanyFilesInfoParams,
  options?: TUseQueryOptions<TGetIdentityCompanyFilesInfoResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'IdentityCompanyFilesInfo', 'GET', ...Object.values(params || {})],
    queryFn: () => getIdentityCompanyFilesInfo(params, options?.meta?.requestOptions),
  })
}

// 企业认证-保存上传文件草稿
export const usePostIdentityCompanySaveFilesMutation = (
  mutationOptions?: TUseMutationOptions<TPostIdentityCompanySaveFilesResponse, TPostIdentityCompanySaveFilesParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'IdentityCompanySaveFiles', 'Post'],
    mutationFn: (params?: TPostIdentityCompanySaveFilesParams) =>
      postIdentityCompanySaveFiles(params, mutationOptions?.meta?.requestOptions),
  })
}

// 个人标准认证
export const usePostIdentityStandardApplyMutation = (
  mutationOptions?: TUseMutationOptions<TPostIdentityStandardApplyResponse, TPostIdentityStandardApplyParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'IdentityStandardApply', 'Post'],
    mutationFn: (params?: TPostIdentityStandardApplyParams) =>
      postIdentityStandardApply(params, mutationOptions?.meta?.requestOptions),
  })
}

// 个人高级认证
export const usePostIdentityAdvancedApplyMutation = (
  mutationOptions?: TUseMutationOptions<TPostIdentityAdvancedApplyResponse, TPostIdentityAdvancedApplyParams> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'IdentityAdvancedApply', 'Post'],
    mutationFn: (params?: TPostIdentityAdvancedApplyParams) =>
      postIdentityAdvancedApply(params, mutationOptions?.meta?.requestOptions),
  })
}

// 企业认证-查询认证最终收益权人草稿
export const useGetIdentityCompanyBeneficiaryInfoQuery = (
  params?: TGetIdentityCompanyBeneficiaryInfoParams,
  options?: TUseQueryOptions<TGetIdentityCompanyBeneficiaryInfoResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'IdentityCompanyBeneficiaryInfo', 'GET', ...Object.values(params || {})],
    queryFn: () => getIdentityCompanyBeneficiaryInfo(params, options?.meta?.requestOptions),
  })
}

// 企业认证-查询认证董事草稿
export const useGetIdentityCompanyDirectorInfoQuery = (
  params?: TGetIdentityCompanyDirectorInfoParams,
  options?: TUseQueryOptions<TGetIdentityCompanyDirectorInfoResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'IdentityCompanyDirectorInfo', 'GET', ...Object.values(params || {})],
    queryFn: () => getIdentityCompanyDirectorInfo(params, options?.meta?.requestOptions),
  })
}

// 企业认证-保存认证最终收益权人草稿
export const usePostIdentityCompanySaveBeneficiaryMutation = (
  mutationOptions?: TUseMutationOptions<
    TPostIdentityCompanySaveBeneficiaryResponse,
    TPostIdentityCompanySaveBeneficiaryParams
  > & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'IdentityCompanySaveBeneficiary', 'Post'],
    mutationFn: (params?: TPostIdentityCompanySaveBeneficiaryParams) =>
      postIdentityCompanySaveBeneficiary(params, mutationOptions?.meta?.requestOptions),
  })
}

// 企业认证-保存认证董事草稿
export const usePostIdentityCompanySaveDirectorMutation = (
  mutationOptions?: TUseMutationOptions<
    TPostIdentityCompanySaveDirectorResponse,
    TPostIdentityCompanySaveDirectorParams
  > & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'IdentityCompanySaveDirector', 'Post'],
    mutationFn: (params?: TPostIdentityCompanySaveDirectorParams) =>
      postIdentityCompanySaveDirector(params, mutationOptions?.meta?.requestOptions),
  })
}

// 企业认证-保存认证账户交易员草稿
export const usePostIdentityCompanySaveTraderMutation = (
  mutationOptions?: TUseMutationOptions<
    TPostIdentityCompanySaveTraderResponse,
    TPostIdentityCompanySaveTraderParams
  > & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'IdentityCompanySaveTrader', 'Post'],
    mutationFn: (params?: TPostIdentityCompanySaveTraderParams) =>
      postIdentityCompanySaveTrader(params, mutationOptions?.meta?.requestOptions),
  })
}

// 企业认证-查询认证账户交易员草稿
export const useGetIdentityCompanyTraderInfoQuery = (
  params?: TGetIdentityCompanyTraderInfoParams,
  options?: TUseQueryOptions<TGetIdentityCompanyTraderInfoResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'IdentityCompanyTraderInfo', 'GET', ...Object.values(params || {})],
    queryFn: () => getIdentityCompanyTraderInfo(params, options?.meta?.requestOptions),
  })
}

// 用户认证状态
export const useGetIdentityStatusQuery = (
  params?: TGetIdentityStatusParams,
  options?: TUseQueryOptions<TGetIdentityStatusResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'IdentityStatus', 'GET', ...Object.values(params || {})],
    queryFn: () => getIdentityStatus(params, options?.meta?.requestOptions),
  })
}

// 查看不同用户类型所拥有的权益
export const useGetIdentityGetUserRightsQuery = (
  params?: TGetIdentityGetUserRightsParams,
  options?: TUseQueryOptions<TGetIdentityGetUserRightsResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'IdentityGetUserRights', 'GET', ...Object.values(params || {})],
    queryFn: () => getIdentityGetUserRights(params, options?.meta?.requestOptions),
  })
}

// 查询认证结果
export const useGetIdentityGetVerifyResultQuery = (
  params?: TGetIdentityGetVerifyResultParams,
  options?: TUseQueryOptions<TGetIdentityGetVerifyResultResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'IdentityGetVerifyResult', 'GET', ...Object.values(params || {})],
    queryFn: () => getIdentityGetVerifyResult(params, options?.meta?.requestOptions),
  })
}

// 企业认证-查询基础信息草稿
export const useGetIdentityCompanyBasicInfoQuery = (
  params?: TGetIdentityCompanyBasicInfoParams,
  options?: TUseQueryOptions<TGetIdentityCompanyBasicInfoResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'IdentityCompanyBasicInfo', 'GET', ...Object.values(params || {})],
    queryFn: () => getIdentityCompanyBasicInfo(params, options?.meta?.requestOptions),
  })
}

// 企业认证-保存基础信息草稿
export const usePostIdentityCompanySaveBasicInfoMutation = (
  mutationOptions?: TUseMutationOptions<
    TPostIdentityCompanySaveBasicInfoResponse,
    TPostIdentityCompanySaveBasicInfoParams
  > & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useMutation({
    ...mutationOptions,
    mutationKey: ['RealPersonAuth', 'IdentityCompanySaveBasicInfo', 'Post'],
    mutationFn: (params?: TPostIdentityCompanySaveBasicInfoParams) =>
      postIdentityCompanySaveBasicInfo(params, mutationOptions?.meta?.requestOptions),
  })
}

// 企业认证-示例文件展示
export const useGetIdentityCompanyTemplateFileQuery = (
  params?: TGetIdentityCompanyTemplateFileParams,
  options?: TUseQueryOptions<TGetIdentityCompanyTemplateFileResponse> & {
    meta?: TCustomUseQueryMeta
  }
) => {
  return useQuery({
    ...options,
    queryKey: ['RealPersonAuth', 'IdentityCompanyTemplateFile', 'GET', ...Object.values(params || {})],
    queryFn: () => getIdentityCompanyTemplateFile(params, options?.meta?.requestOptions),
  })
}
